import React, { useState, useEffect } from 'react'
import {
  FileText,
  // Download, // Commented out - PDF download returns 501 currently
  ExternalLink,
  RefreshCw,
  Loader2,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle
} from 'lucide-react'
import { Button } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import { invoiceService, Invoice } from '../../services/invoiceService'

const InvoicesSection: React.FC = () => {
  const { showError, showSuccess } = useToast()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [retryingId, setRetryingId] = useState<string | null>(null)

  const fetchInvoices = async () => {
    try {
      setLoading(true)
      const response = await invoiceService.getInvoices({ limit: 10 })

      if (response.success) {
        setInvoices(response.data || [])
      } else {
        showError('Failed to load invoices', response.message || 'Please try again')
      }
    } catch (error) {
      console.error('Error fetching invoices:', error)
      showError('Error', 'Failed to load invoices')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchInvoices()
  }, [])

  const handleRetryPayment = async (invoiceId: string) => {
    try {
      setRetryingId(invoiceId)
      const response = await invoiceService.retryPayment(invoiceId)

      if (response.success) {
        showSuccess('Payment Successful', 'Your payment has been processed')
        await fetchInvoices()
      } else {
        showError('Payment Failed', response.message || 'Please try again or update your payment method')
      }
    } catch (error) {
      console.error('Error retrying payment:', error)
      showError('Error', 'Failed to process payment')
    } finally {
      setRetryingId(null)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid':
        return <CheckCircle2 className='w-4 h-4 text-green-500' />
      case 'open':
        return <Clock className='w-4 h-4 text-yellow-500' />
      case 'void':
        return <XCircle className='w-4 h-4 text-gray-400' />
      case 'uncollectible':
        return <AlertCircle className='w-4 h-4 text-red-500' />
      default:
        return <FileText className='w-4 h-4 text-gray-400' />
    }
  }

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      paid: 'bg-green-50 text-green-700 border-green-200',
      open: 'bg-yellow-50 text-yellow-700 border-yellow-200',
      void: 'bg-gray-50 text-gray-600 border-gray-200',
      draft: 'bg-gray-50 text-gray-600 border-gray-200',
      uncollectible: 'bg-red-50 text-red-700 border-red-200'
    }

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border ${
          styles[status] || styles.draft
        }`}
      >
        {getStatusIcon(status)}
        <span className='capitalize'>{status}</span>
      </span>
    )
  }

  const formatCurrency = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <div className='p-5 bg-gray-50 rounded-lg border border-gray-200'>
        <div className='flex items-center justify-center py-4'>
          <Loader2 className='w-5 h-5 animate-spin text-gray-500' />
          <span className='ml-2 text-sm text-gray-500'>Loading invoices...</span>
        </div>
      </div>
    )
  }

  if (invoices.length === 0) {
    return (
      <div className='p-5 bg-gray-50 rounded-lg border border-gray-200'>
        <div className='flex items-center gap-4'>
          <div className='p-2.5 bg-white rounded-lg border border-gray-200'>
            <FileText className='h-5 w-5 text-gray-400' />
          </div>
          <div>
            <p className='text-sm font-medium text-gray-900'>No invoices yet</p>
            <p className='text-xs text-gray-500 mt-0.5'>
              Invoices will appear here once you have an active subscription
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className='space-y-3'>
      {/* Invoice List */}
      <div className='border border-gray-200 rounded-lg overflow-hidden'>
        <table className='w-full'>
          <thead className='bg-gray-50'>
            <tr>
              <th className='px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                Invoice
              </th>
              <th className='px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                Date
              </th>
              <th className='px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                Amount
              </th>
              <th className='px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                Status
              </th>
              <th className='px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {invoices.map((invoice) => (
              <tr key={invoice.id} className='hover:bg-gray-50'>
                <td className='px-4 py-3'>
                  <div className='flex items-center gap-2'>
                    <FileText className='w-4 h-4 text-gray-400' />
                    <span className='text-sm font-medium text-gray-900'>
                      {invoice.invoice_number || `INV-${invoice.stripe_invoice_id.slice(-8)}`}
                    </span>
                  </div>
                </td>
                <td className='px-4 py-3'>
                  <span className='text-sm text-gray-600'>
                    {formatDate(invoice.invoice_date)}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  <span className='text-sm font-medium text-gray-900'>
                    {formatCurrency(invoice.total, invoice.currency)}
                  </span>
                </td>
                <td className='px-4 py-3'>
                  {getStatusBadge(invoice.status)}
                </td>
                <td className='px-4 py-3'>
                  <div className='flex items-center justify-end gap-2'>
                    {/* Retry Payment - only for open/failed invoices */}
                    {invoice.status === 'open' && invoice.amount_due > 0 && (
                      <Button
                        variant='outline'
                        size='xs'
                        onClick={() => handleRetryPayment(invoice.stripe_invoice_id)}
                        disabled={retryingId === invoice.stripe_invoice_id}
                        className='text-xs'
                      >
                        {retryingId === invoice.stripe_invoice_id ? (
                          <Loader2 className='w-3 h-3 animate-spin mr-1' />
                        ) : (
                          <RefreshCw className='w-3 h-3 mr-1' />
                        )}
                        Pay Now
                      </Button>
                    )}

                    {/* View Invoice */}
                    {invoice.hosted_invoice_url && (
                      <Button
                        variant='ghost'
                        size='xs'
                        onClick={() => window.open(invoice.hosted_invoice_url!, '_blank')}
                        title='View Invoice'
                      >
                        <ExternalLink className='w-4 h-4' />
                      </Button>
                    )}

                    {/* Download PDF - Commented out per docs (returns 501 currently)
                    {invoice.invoice_pdf_url && (
                      <Button
                        variant='ghost'
                        size='xs'
                        onClick={() => window.open(invoice.invoice_pdf_url!, '_blank')}
                        title='Download PDF'
                      >
                        <Download className='w-4 h-4' />
                      </Button>
                    )}
                    */}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Show More Link */}
      {invoices.length >= 10 && (
        <div className='text-center'>
          <Button
            variant='ghost'
            size='xs'
            onClick={() => {
              // Could navigate to full invoice history page
              // For now, just show a message
              showSuccess('View All', 'Full invoice history page coming soon')
            }}
            className='text-xs text-gray-500'
          >
            View all invoices
          </Button>
        </div>
      )}
    </div>
  )
}

export default InvoicesSection
